import { action, makeObservable, observable } from "mobx";
import { BriaAxios } from "../config/axios.tsx";

export interface IAnonymousUsageStore {
	getUsage(feature: string): { used: number; limit: number };
	getRemaining(feature: string): number;
	isLimitReached(feature: string): boolean;
	setFeatureUsage(feature: string, used: number, limit: number): void;
	setBulkUsage(data: Record<string, { used: number; limit: number }>): void;
	incrementFeatureUsage(feature: string): void;
	consumeToken(feature: string): Promise<void>;
	refreshUsage(): Promise<void>;
	limitReachedFeature: string | null;
	setLimitReachedFeature(feature: string | null): void;
}

const DEFAULT_USAGE = { used: 0, limit: 12 };

export default class AnonymousUsageStore implements IAnonymousUsageStore {
	usageMap: Map<string, { used: number; limit: number }> = new Map();
	limitReachedFeature: string | null = null;

	constructor() {
		makeObservable(this, {
			usageMap: observable,
			limitReachedFeature: observable,
			setFeatureUsage: action,
			setBulkUsage: action,
			incrementFeatureUsage: action,
			setLimitReachedFeature: action,
		});
	}

	getUsage = (feature: string): { used: number; limit: number } => {
		return this.usageMap.get(feature) ?? { ...DEFAULT_USAGE };
	};

	getRemaining = (feature: string): number => {
		const usage = this.getUsage(feature);
		return Math.max(0, usage.limit - usage.used);
	};

	isLimitReached = (feature: string): boolean => {
		const usage = this.getUsage(feature);
		return usage.used >= usage.limit;
	};

	setFeatureUsage = (feature: string, used: number, limit: number): void => {
		this.usageMap.set(feature, { used, limit });
	};

	setBulkUsage = (data: Record<string, { used: number; limit: number }>): void => {
		for (const [feature, usage] of Object.entries(data)) {
			this.usageMap.set(feature, usage);
		}
	};

	incrementFeatureUsage = (feature: string): void => {
		const current = this.getUsage(feature);
		this.usageMap.set(feature, { ...current, used: current.used + 1 });
	};

	setLimitReachedFeature = (feature: string | null): void => {
		this.limitReachedFeature = feature;
	};

	consumeToken = async (feature: string): Promise<void> => {
		try {
			const axios = await BriaAxios();
			const response = await axios.post("/anonymous_usage/consume", { feature });
			const { features } = response.data;
			this.setBulkUsage(features);
		} catch (error) {
			console.error("Failed to consume anonymous token:", error);
		}
	};

	refreshUsage = async (): Promise<void> => {
		try {
			const axios = await BriaAxios();
			const response = await axios.get("/anonymous_usage");
			const { features } = response.data;
			this.setBulkUsage(features);
		} catch (error) {
			console.error("Failed to refresh anonymous usage:", error);
		}
	};
}
